import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Pause, Play } from "lucide-react";
import { SCREENS } from "../../data/site";
import PhoneMockup from "./PhoneMockup";

const STEP_MS = 5000;
const HOLD_MS = 12000;

export default function SutraExplorer({ className = "", footer = null }) {
  const reduce = useReducedMotion();
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const [inView, setInView] = useState(false);
  const [hover, setHover] = useState(false);
  const [hold, setHold] = useState(0);
  const [paused, setPaused] = useState(false);
  const playing = !reduce && inView && !hover && !hold && !paused;

  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.3 });
    io.observe(rootRef.current);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!playing) return;
    const t = setTimeout(() => setIdx((i) => (i + 1) % SCREENS.length), STEP_MS);
    return () => clearTimeout(t);
  }, [playing, idx]);

  useEffect(() => {
    if (!hold) return;
    const t = setTimeout(() => setHold(0), HOLD_MS);
    return () => clearTimeout(t);
  }, [hold]);

  useEffect(() => {
    const list = listRef.current;
    const chip = list?.children[idx];
    if (!list || !chip || list.scrollWidth <= list.clientWidth) return;
    list.scrollTo({ left: chip.offsetLeft - list.clientWidth / 2 + chip.clientWidth / 2, behavior: reduce ? "auto" : "smooth" });
  }, [idx, reduce]);

  const select = (i) => {
    setIdx(i);
    setHold((h) => h + 1);
  };
  const s = SCREENS[idx];
  const mouse = (v) => (e) => e.pointerType === "mouse" && setHover(v);

  return (
    <div ref={rootRef} className={`grid items-center gap-12 md:grid-cols-2 ${className}`} onPointerEnter={mouse(true)} onPointerLeave={mouse(false)} data-testid="sutra-interactive">
      <div className="md:col-span-2" data-testid="sutra-explore-heading">
        <h3 className="font-display text-3xl text-ink sm:text-4xl">Explore the Practice</h3>
        <p className="mt-3 max-w-xl text-base leading-relaxed text-ink-soft">Take a look inside the app and discover how each sutra unfolds through reflection, understanding and meditation.</p>
      </div>
      <div className="relative flex justify-center">
        <div aria-hidden className="sun-glow pointer-events-none absolute left-1/2 top-[38%] h-[340px] w-[340px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(224,122,85,0.32),rgba(234,223,207,0.4)_45%,transparent_70%)] sm:h-[420px] sm:w-[420px]" />
        <PhoneMockup idx={idx} />
      </div>

      <div className="min-w-0">
        <div className="flex items-center justify-between gap-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-ink-soft">
            <span className="tabular-nums text-coral-ink">{String(idx + 1).padStart(2, "0")}</span> / 08 · Inside a practice
          </p>
          {!reduce && (
            <button
              type="button"
              onClick={() => setPaused((p) => !p)}
              aria-label={paused ? "Play app walkthrough" : "Pause app walkthrough"}
              data-testid="sutra-autoplay-toggle"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-ink/15 text-ink transition-colors hover:bg-ink hover:text-ivory"
            >
              {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
            </button>
          )}
        </div>

        <div className="mt-5 min-h-[150px] sm:min-h-[130px]" data-testid="sutra-caption">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div key={s.id} initial={reduce ? false : { opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={reduce ? undefined : { opacity: 0, y: -8 }} transition={{ duration: 0.45 }}>
              <h3 className="font-display text-3xl text-ink" data-testid="sutra-caption-title">{s.label}</h3>
              <p className="mt-3 text-base leading-relaxed text-ink-soft" data-testid="sutra-caption-text">{s.caption}</p>
            </motion.div>
          </AnimatePresence>
        </div>

        <ol
          ref={listRef}
          aria-label="App steps"
          className="no-scrollbar -mx-6 mt-6 flex snap-x gap-2 overflow-x-auto px-6 pb-2 md:mx-0 md:flex-col md:gap-0 md:overflow-visible md:px-0 md:pb-0"
        >
          {SCREENS.map((sc, i) => {
            const on = i === idx;
            return (
              <li key={sc.id} className="shrink-0 snap-center md:border-t md:border-ink/10 md:last:border-b">
                <button
                  type="button"
                  onClick={() => select(i)}
                  aria-current={on ? "step" : undefined}
                  data-testid={`sutra-app-step-${sc.id}`}
                  className={`relative flex min-h-[44px] w-full items-center gap-3 overflow-hidden rounded-full border px-4 text-left text-[13px] font-medium transition-colors duration-300 md:rounded-none md:border-0 md:px-0 md:py-1 ${
                    on ? "border-ink bg-ink text-ivory md:bg-transparent md:text-ink" : "border-ink/15 text-ink-soft hover:text-ink"
                  }`}
                >
                  <span className={`hidden w-6 tabular-nums md:inline ${on ? "text-coral-ink" : "text-ink/40"}`}>{String(i + 1).padStart(2, "0")}</span>
                  <span className="whitespace-nowrap uppercase tracking-[0.16em]">{sc.label}</span>
                  <span aria-hidden className={`ml-auto hidden h-1.5 w-1.5 rounded-full bg-coral transition-opacity duration-300 md:block ${on ? "opacity-100" : "opacity-0"}`} />
                  {on && (
                    <span
                      aria-hidden
                      key={`${idx}-${playing}`}
                      className={`absolute bottom-0 left-0 hidden h-px w-full origin-left bg-coral-ink md:block ${playing ? "step-progress" : ""}`}
                      style={{ animationDuration: `${STEP_MS}ms` }}
                    />
                  )}
                </button>
              </li>
            );
          })}
        </ol>
        {footer}
      </div>
    </div>
  );
}
